"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MobileBottomNav } from "./MobileBottomNav";
import { PwaInstallPrompt } from "@/components/PwaInstallPrompt";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useLanguage } from "@/lib/useLanguage";
import { MenuHunterLockup } from "@/components/brand/MenuHunterLockup";

const HIDE_SHELL_PREFIXES = ["/login", "/auth"];

export function ProductShell({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? "";
  const { t } = useLanguage();

  const hideShell = HIDE_SHELL_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );

  if (hideShell) {
    return <>{children}</>;
  }

  const isExplore = pathname.startsWith("/explore");
  const isFullBleed = isExplore;
  const goHome = t.common?.goHome ?? "Ir al inicio";

  return (
    <div className="min-h-dvh flex flex-col bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <header
        className={`${
          isExplore ? "hidden md:flex" : "flex"
        } items-center justify-between gap-4 px-4 md:px-6 py-2.5 md:py-3 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 backdrop-blur z-30 shrink-0`}
      >
        <div className="flex items-center gap-4 md:gap-8 min-w-0">
          <Link
            href="/"
            className="flex items-center gap-2 font-semibold text-base md:text-lg shrink-0"
            aria-label={goHome}
          >
            <MenuHunterLockup
              markSize={42}
              strokeWidth={15.5}
              variant="micro"
              wordmarkClassName="font-semibold text-[#0F172A] dark:text-white text-base md:text-lg hidden min-[400px]:inline"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <NavLink href="/explore" active={pathname.startsWith("/explore")}>
              {t.product?.explore ?? "Explore"}
            </NavLink>
            <NavLink href="/search" active={pathname.startsWith("/search")}>
              {t.product?.search ?? "Search"}
            </NavLink>
            <NavLink href="/account/saved" active={pathname.startsWith("/account/saved")}>
              {t.product?.saved ?? "Saved"}
            </NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <Link
            href="/download"
            className="hidden sm:inline text-sm text-accent hover:text-accentDark font-medium"
          >
            {t.product?.getApp ?? "Get the app"}
          </Link>
          <LanguageToggle />
          <ThemeToggle />
          <Link
            href="/account"
            className="hidden md:inline text-sm px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {t.product?.account ?? "Account"}
          </Link>
        </div>
      </header>

      <main
        className={`flex-1 flex flex-col min-h-0 ${
          isFullBleed
            ? "pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0"
            : "pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-8"
        }`}
      >
        {children}
      </main>

      <MobileBottomNav />
      <PwaInstallPrompt />
    </div>
  );
}

function NavLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={`px-3 py-2 rounded-lg transition-colors ${
        active
          ? "bg-accent/15 text-accentDark dark:text-accent font-medium"
          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
      }`}
    >
      {children}
    </Link>
  );
}
